import Modal from "common/Modal/Modal";
import React from "react";
import Text from "common/Typography/Text";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import { useTheme } from "styled-components";
import { sharedLib } from "@shared/services/shared.service";
import { useAddJobToast } from "@shared/utils/hooks/useAddJobToast";
import { JobToastType } from "@shared/slices/jobs.slice";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";

interface BulkCancelSignalsModalProps {
  open: boolean;
  signalIDs?: number[];
  closeModal: () => void;
  onFinish?: () => void;
}

const BulkCancelSignalsModal = ({
  open,
  signalIDs,
  closeModal,
  onFinish,
}: BulkCancelSignalsModalProps) => {
  const appTheme = useTheme();
  const { t } = useTypedTranslation(LanguageNameSpaces.SIGNALS);
  const [bulkCancelSignals, bulkCancelSignalsStatus] =
    sharedLib.sharedApi.signals.useBulkCancelSignalsJobMutation();
  const { addJobToast, removeJobToast } = useAddJobToast(JobToastType.Signal);
  const isLoading = bulkCancelSignalsStatus.isLoading;

  const onOkPress = () => {
    if (!signalIDs?.length) return;
    const args = { signal_ids: signalIDs };
    const id = addJobToast(args);
    closeModal();
    bulkCancelSignals(args)
      .unwrap()
      .catch(() => {})
      .finally(() => {
        removeJobToast(id);
        onFinish?.();
      });
  };

  return (
    <Modal
      onCancel={closeModal}
      open={open}
      title={t("bulk_cancel_progress.close_signals_title")}
      onOk={onOkPress}
      okText={t("bulk_cancel_progress.close_signals_title")}
      cancelText={t("bulk_cancel_progress.cancel")}
      okButtonProps={{ loading: isLoading }}
      cancelButtonProps={{ disabled: isLoading }}
      destroyOnHidden={true}
    >
      <LoadingSpinnerOverlay
        isLoading={isLoading}
        hideContentWhileLoading={false}
        wrap={true}
        backgroundColor={appTheme.colors.cardBgColor}
      >
        <Text>
          {t("bulk_cancel_progress.confirm_close", {
            count: signalIDs?.length ?? 0,
          })}
        </Text>
      </LoadingSpinnerOverlay>
    </Modal>
  );
};

export default React.memo(BulkCancelSignalsModal);
